import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import { FormattedError } from '@app/common/Components/FormattedError'
import {
  AlertType,
  useNotifications
} from '@app/common/services/Notifications/NotificationsProvider'
import {
  fetchDefaultValues,
  InstanceDefaultValuesMap
} from '@app/modules/CourtCases/Components/CourtCaseInvoicingForm/Components/InvoicingDefaultValuesContext/fetchDefaultValues'

export interface InvoicingDefaultValuesContextValues {
  loadingDefaultValues: boolean
  setDefaultValues: () => void
}

const InvoicingDefaultValuesContext = createContext<InvoicingDefaultValuesContextValues>(
  null as unknown as InvoicingDefaultValuesContextValues
)

export const InvoicingDefaultValuesProvider = ({ children }: { children?: React.ReactNode }) => {
  const { getValues, setValue } = useFormContext()
  const [loadingDefaultValues, setLoadingDefaultValues] = useState(true)
  const [defaultValues, setDefaultValuesState] = useState<InstanceDefaultValuesMap>({})
  const { pushNotification } = useNotifications()

  useEffect(() => {
    fetchDefaultValues()
      .then((parsedDefaults) => setDefaultValuesState(parsedDefaults))
      .catch((error) =>
        pushNotification({
          type: AlertType.error,
          message: <FormattedError error={error} />
        })
      )
      .finally(() => setLoadingDefaultValues(false))
  }, [])

  const setDefaultValues = useCallback(() => {
    const selectedKey = getValues('Instance') as unknown as string | undefined

    if (!selectedKey) {
      return
    }

    const defaults = defaultValues[selectedKey]

    if (!defaults) {
      return
    }

    for (const [key, value] of Object.entries(defaults)) {
      setValue(key, value)
    }
  }, [getValues, setValue, defaultValues])

  return (
    <InvoicingDefaultValuesContext.Provider
      value={{
        loadingDefaultValues,
        setDefaultValues
      }}
    >
      {children}
    </InvoicingDefaultValuesContext.Provider>
  )
}

export const useInvoicingDefaultValuesContext = () => useContext(InvoicingDefaultValuesContext)
